// src/pages/DashboardPage.jsx
// re-push trigger

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, TextField, CircularProgress, Divider,
  MenuItem, Stack, IconButton, Tooltip, InputAdornment, Avatar, Chip, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, useTheme, useMediaQuery, Drawer
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TodayIcon from '@mui/icons-material/Today';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ShareIcon from '@mui/icons-material/Share';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, LineElement, PointElement, CategoryScale, LinearScale, Tooltip as ChartTooltip, Legend
} from 'chart.js';

import ThemeToggle from '../components/ThemeToggle';
import {
  getLinkedStudents,
  getProgressOverTime,
  getAnswerHistory,
  getClassRecords,
  addClassRecord,
  deleteClassRecord,
  getFilteredReport,
  createQuestion,
  getQuestionsForTeacher
} from '../api/reportApi';
import { getLessonStudentsMasid } from '../api/reportApi';
import { buildStudentReportHtml } from '../utils/generateStudentHtml';
import { downloadTextFile } from '../utils/downloadFile';

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, ChartTooltip, Legend);

// helpers
const sanitizeFileName = (name) =>
  name ? String(name).replace(/[\\\/:*?"<>|]+/g, '').trim().replace(/\s+/g, '_') : 'report';

const persianType = (type) => {
  if (!type) return '';
  switch (String(type).toLowerCase()) {
    case 'positive': return 'مثبت';
    case 'negative': return 'منفی';
    case 'classexam': return 'امتحان کلاسی';
    case 'note': return 'یادداشت';
    default: return type;
  }
};

const compactDate = (d) => {
  try {
    if (d === null || d === undefined || d === '') return '—';
    const s = String(d).trim();
    const parsed = Date.parse(s);
    if (!isNaN(parsed)) return new Date(parsed).toLocaleDateString();
    if (/^\d+$/.test(s)) {
      if (s.length === 10) return new Date(Number(s) * 1000).toLocaleDateString();
      if (s.length === 13) return new Date(Number(s)).toLocaleDateString();
    }
    return s || '—';
  } catch { return '—'; }
};

const normalizeLesson = (l) => ({ id: l.Id ?? l.id ?? l._id ?? l.lessonId, title: l.Title ?? l.title ?? l.name });

export default function DashboardPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const email = localStorage.getItem('teacherEmail') || 'معلم';

  // layout
  const headerHeight = isSmDown ? 112 : 140; // slightly reduced for small screens
  const leftColumnWidth = isMdDown ? '100%' : 300;
  const leftColumnMaxWidth = isMdDown ? '100%' : 300;

  // core states
  const [students, setStudents] = useState([]); // all linked-students
  const [q, setQ] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentsOpen, setStudentsOpen] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // detail states
  const [progressData, setProgressData] = useState([]);
  const [answerHistory, setAnswerHistory] = useState([]);
  const [classRecords, setClassRecords] = useState([]);

  const [loading, setLoading] = useState(false);
  const [recordsLoading, setRecordsLoading] = useState(false);

  // lessons + filter
  const [lessons, setLessons] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [lessonsError, setLessonsError] = useState('');
  const [selectedLessonId, setSelectedLessonId] = useState(''); // dropdown filter

  // lesson-students (server endpoint students-masid)
  const [lessonStudents, setLessonStudents] = useState([]);
  const [lessonStudentsMeta, setLessonStudentsMeta] = useState({ totalCount: 0, page: 1, pageSize: 50 });
  const [lessonStudentsLoading, setLessonStudentsLoading] = useState(false);
  const [lessonStudentsError, setLessonStudentsError] = useState(null);

  // question modal
  const [questionModalOpen, setQuestionModalOpen] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionForm, setQuestionForm] = useState({
    content: '', optionA: '', optionB: '', optionC: '', optionD: '',
    correctOption: 'A', lessonId: '', expirationDate: ''
  });

  // class record form (editable)
  const [newRecord, setNewRecord] = useState({
    type: 'Positive',
    lessonId: '',
    date: '',
    description: '',
    score: ''
  });

  // student lessons for form & cache (when opening student)
  const [studentLessonsForForm, setStudentLessonsForForm] = useState([]);

  // initial load: linked students + lessons (from by-teacher and student.lessons union)
  useEffect(() => {
    (async () => {
      try {
        // linked students
        const sList = await getLinkedStudents();
        const studentsArr = Array.isArray(sList) ? sList : [];
        setStudents(studentsArr);

        const token = localStorage.getItem('teacherToken');
        if (!token) {
          setLessons([]);
          setLessonsError('توکن معلم یافت نشد. لطفا وارد شوید.');
          return;
        }

        setLessonsLoading(true);
        setLessonsError('');
        let teacherLessons = [];
        try {
          const res = await axios.get(`${process.env.REACT_APP_API_BASE || 'https://education-backend.liara.run/api'}/lesson/by-teacher`, {
            headers: { Authorization: `Bearer ${token}` },
            validateStatus: s => s < 500
          });
          if (res.status === 200) {
            const arr = Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.items ?? []);
            teacherLessons = Array.isArray(arr) ? arr.map(normalizeLesson) : [];
          }
        } catch {
          teacherLessons = [];
        }

        const lessonsFromStudents = [];
        for (const s of studentsArr) {
          const ls = Array.isArray(s.lessons) ? s.lessons : [];
          for (const l of ls) lessonsFromStudents.push(normalizeLesson(l));
        }

        const map = new Map();
        for (const l of teacherLessons) if (l?.id) map.set(String(l.id), l);
        for (const l of lessonsFromStudents) if (l?.id) map.set(String(l.id), l);

        setLessons(Array.from(map.values()));
      } catch {
        setLessons([]);
      } finally {
        setLessonsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fetch lesson-students from new endpoint
  const fetchLessonStudents = useCallback(async ({ lessonId, page = 1, pageSize = 50, search = '', includeRecords = false, teacherScoped = true } = {}) => {
    if (!lessonId) {
      setLessonStudents([]);
      setLessonStudentsMeta({ totalCount: 0, page: 1, pageSize });
      return;
    }

    setLessonStudentsLoading(true);
    setLessonStudentsError(null);
    try {
      const res = await getLessonStudentsMasid(lessonId, { page, pageSize, search, includeRecords, teacherScoped });
      if (res.status >= 200 && res.status < 300) {
        const data = res.data;
        setLessonStudents(Array.isArray(data.students) ? data.students : []);
        setLessonStudentsMeta({ totalCount: data.totalCount ?? 0, page: data.page ?? page, pageSize: data.pageSize ?? pageSize });
      } else if (res.status === 401) {
        setLessonStudentsError('عدم دسترسی. لطفا وارد شوید.');
      } else {
        setLessonStudentsError(res.data?.message || 'خطا در دریافت لیست دانش‌آموزان');
      }
    } catch (err) {
      console.error('fetchLessonStudents error', err);
      setLessonStudentsError('خطای شبکه یا سرور');
    } finally {
      setLessonStudentsLoading(false);
    }
  }, []);

  // when lesson selection changes, fetch page 1
  useEffect(() => {
    if (!selectedLessonId) {
      setLessonStudents([]);
      setLessonStudentsMeta({ totalCount: 0, page: 1, pageSize: 50 });
      setLessonStudentsError(null);
      return;
    }
    fetchLessonStudents({ lessonId: selectedLessonId, page: 1, pageSize: lessonStudentsMeta.pageSize, includeRecords: false, teacherScoped: true });
  }, [selectedLessonId]); // eslint-disable-line react-hooks/exhaustive-deps

  // visibleStudents: if lesson selected, use lessonStudents; otherwise use linked students
  const visibleStudents = useMemo(() => {
    const term = (q || '').trim().toLowerCase();
    const matchesSearch = (s) => {
      if (!term) return true;
      return (s.name || '').toLowerCase().includes(term) || (s.code || '').toLowerCase().includes(term);
    };

    if (selectedLessonId) {
      return lessonStudents.filter(matchesSearch);
    }
    return students.filter(matchesSearch);
  }, [students, lessonStudents, q, selectedLessonId]);

  // load class records helper
  const loadClassRecords = useCallback(async (studentId) => {
    setRecordsLoading(true);
    try {
      const recs = await getClassRecords(studentId);
      const normalized = (recs || []).map(r => ({
        id: r.id ?? r.Id,
        studentId: r.studentId ?? r.StudentId,
        date: r.date ?? r.Date,
        type: r.type ?? r.Type,
        description: r.description ?? r.Description,
        score: r.score ?? r.Score,
        lessonId: r.lessonId ?? r.LessonId,
        lessonTitle: r.lessonTitle ?? r.LessonTitle ?? (r.lesson ? (r.lesson.title || r.lesson.Title) : undefined)
      }));
      setClassRecords(normalized);
    } catch {
      setClassRecords([]);
    } finally {
      setRecordsLoading(false);
    }
  }, []);

  const selectStudent = async (studentId) => {
    setSelectedStudentId(studentId);
    const found = (students.find(s => String(s.id) === String(studentId)) || lessonStudents.find(s => String(s.id) === String(studentId))) || null;
    setSelectedStudent(found);
    setLoading(true);
    setProgressData([]); setAnswerHistory([]); setClassRecords([]);
    setStudentLessonsForForm([]);
    setNewRecord({ type: 'Positive', lessonId: '', date: '', description: '', score: '' });

    if (isMdDown) {
      setDrawerOpen(true);
      setStudentsOpen(false);
    }

    try {
      const [prog, answers] = await Promise.all([getProgressOverTime(studentId), getAnswerHistory(studentId)]);
      setProgressData(prog || []);
      const normalizedAnswers = (answers || []).map(a => ({
        ...a,
        answerTime: a.answerTime ?? a.AnswerTime ?? a.date ?? a.Date ?? a.createdAt ?? a.CreatedAt ?? null
      }));
      setAnswerHistory(normalizedAnswers);
      await loadClassRecords(studentId);

      // fetch student lessons for form (best-effort)
      try {
        const token = localStorage.getItem('teacherToken');
        const res = await axios.get(`${process.env.REACT_APP_API_BASE || 'https://education-backend.liara.run/api'}/student/${studentId}/lessons`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const arr = Array.isArray(res.data) ? res.data : [];
        const normalized = arr.map(x => ({ id: x.Id ?? x.id, title: x.Title ?? x.title ?? x.name }));

        // only lessons teacher has
        const teacherLessonIds = new Set((lessons || []).map(l => String(l.id)));
        const filtered = normalized.filter(x => teacherLessonIds.has(String(x.id)));

        setStudentLessonsForForm(filtered);
      } catch {
        setStudentLessonsForForm([]);
      }
    } catch {
      setProgressData([]); setAnswerHistory([]); setClassRecords([]);
      setStudentLessonsForForm([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRecord = async (newRecordLocal, selectedStudentIdLocal) => {
    if (!selectedStudentIdLocal) return alert('ابتدا یک دانش‌آموز انتخاب کنید.');
    // minimal validation
    if (!newRecordLocal.type || !String(newRecordLocal.description || '').trim()) {
      return alert('نوع و توضیح را وارد کنید.');
    }
    setLoading(true);
    try {
      const payload = {
        studentId: Number(selectedStudentIdLocal),
        type: newRecordLocal.type,
        date: newRecordLocal.date ? new Date(newRecordLocal.date).toISOString() : undefined,
        description: newRecordLocal.description,
        score: newRecordLocal.score !== '' && newRecordLocal.score !== null && newRecordLocal.score !== undefined
          ? Number(newRecordLocal.score)
          : null,
        lessonId: newRecordLocal.lessonId ? Number(newRecordLocal.lessonId) : undefined
      };
      await addClassRecord(payload);
      await loadClassRecords(selectedStudentIdLocal);
      // reset form (keep lessonId for convenience)
      setNewRecord(prev => ({ type: prev.type, lessonId: prev.lessonId, date: '', description: '', score: '' }));
    } catch (err) {
      console.error('Error adding class record', err);
      alert('خطا در افزودن رکورد.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecord = async (id) => {
    if (!window.confirm('آیا از حذف این رکورد مطمئن هستید؟')) return;
    try {
      await deleteClassRecord(id);
      setClassRecords(prev => prev.filter(r => r.id !== id));
    } catch {
      alert('حذف موفق نبود.');
    }
  };

  const handleDownloadHtmlReport = () => {
    if (!selectedStudentId) return alert('ابتدا یک دانش‌آموز انتخاب کنید.');
    const student = selectedStudent || students.find(s => String(s.id) === String(selectedStudentId)) || { id: selectedStudentId, name: '', code: selectedStudentId };
    const html = buildStudentReportHtml({
      student,
      progress: progressData,
      answers: answerHistory,
      classRecords: classRecords.map(r => ({ ...r, type: persianType(r.type) }))
    });
    const fileName = `${sanitizeFileName(student.name || student.code || student.id)}.html`;
    downloadTextFile(html, fileName);
  };

  const handleShareLink = (student) => {
    const link = `${window.location.origin}/parent-report/${student.id}`;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(() => alert('لینک کپی شد')).catch(() => prompt('کپی کن', link));
    } else {
      prompt('کپی کن', link);
    }
  };

  const handleFilterReport = async (lessonId) => {
    if (!selectedStudentId || !lessonId) return alert('ابتدا دانش‌آموز و شناسه درس را تعیین کنید.');
    try {
      const rep = await getFilteredReport(selectedStudentId, lessonId);
      alert(JSON.stringify(rep));
    } catch {
      alert('دریافت گزارش فیلتر شده موفق نبود.');
    }
  };

  const loadQuestions = async () => {
    setQuestionsLoading(true);
    try {
      const data = await getQuestionsForTeacher();
      setQuestions(data || []);
    } catch {
      setQuestions([]);
    } finally {
      setQuestionsLoading(false);
    }
  };

  const handleOpenQuestionModal = () => {
    setQuestionForm({ content: '', optionA: '', optionB: '', optionC: '', optionD: '', correctOption: 'A', lessonId: '', expirationDate: '' });
    loadQuestions();
    setQuestionModalOpen(true);
  };

  const handleSubmitQuestion = async () => {
    if (!questionForm.content.trim() || !questionForm.lessonId) return alert('سوال و درس لازم است.');
    try {
      const token = localStorage.getItem('teacherToken');
      const jwt = token ? (() => { try { const payload = token.split('.')[1]; return JSON.parse(atob(payload.replace(/-/g,'+').replace(/_/g,'/'))); } catch { return null; } })() : null;
      const teacherIdFromToken = jwt?.id || jwt?.teacherId || jwt?.sub || undefined;
      const expIso = questionForm.expirationDate ? new Date(questionForm.expirationDate).toISOString() : new Date().toISOString();

      const payload = {
        content: questionForm.content.trim(),
        optionA: (questionForm.optionA || '').trim(),
        optionB: (questionForm.optionB || '').trim(),
        optionC: (questionForm.optionC || '').trim(),
        optionD: (questionForm.optionD || '').trim(),
        correctOption: String(questionForm.correctOption || '').trim().slice(0, 1),
        lessonId: Number(questionForm.lessonId),
        expirationDate: expIso,
        ...(teacherIdFromToken ? { teacherId: Number(teacherIdFromToken) } : {})
      };

      await createQuestion(payload);
      setQuestionModalOpen(false);
      await loadQuestions();
      alert('سوال ثبت شد.');
    } catch {
      alert('ثبت سؤال موفق نبود.');
    }
  };

  const handleDeleteQuestion = async (id) => {
    if (!window.confirm('آیا از حذف سوال مطمئن هستید؟')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE || 'https://education-backend.liara.run/api'}/question/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('teacherToken')}` }
      });
    } catch {
      alert('حذف موفق نبود.');
    } finally {
      setQuestions(prev => prev.filter(q => q.id !== id));
    }
  };

  const chartData = {
    labels: progressData.map(p => p.date || p.Date || ''),
    datasets: [
      {
        label: 'درصد موفقیت',
        data: progressData.map(p => p.successRate ?? p.SuccessRate ?? 0),
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.light,
        tension: 0.3,
        fill: false
      }
    ]
  };

  const handleLogout = () => {
    localStorage.removeItem('teacherToken');
    localStorage.removeItem('teacherEmail');
    navigate('/login');
  };

  // Student detail UI
  const StudentDetailContent = () => (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' }, color: 'text.primary' }}>{selectedStudent?.name || '—'}</Typography>
          <Typography variant="caption" color="text.secondary">کد: {selectedStudent?.code || selectedStudentId}</Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ gap: { xs: 0.5, sm: 1 } }}>
          <Button
            startIcon={<DownloadIcon />}
            variant="outlined"
            onClick={handleDownloadHtmlReport}
            sx={{
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              minWidth: { xs: 96, sm: 120 },
              px: { xs: 1, sm: 2 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}
          >
            دانلود HTML
          </Button>

          <Button
            variant="text"
            onClick={handleOpenQuestionModal}
            sx={{ minWidth: { xs: 92, sm: 120 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            ثبت سؤال سریع
          </Button>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'flex-start', mb: 2, flexDirection: isSmDown ? 'column' : 'row' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.primary', fontSize: { xs: '0.9rem', sm: '1rem' } }}>نمودار پیشرفت</Typography>
          {progressData.length === 0 ? <Typography color="text.secondary">داده‌ای برای نمایش وجود ندارد.</Typography> : <Box sx={{ height: { xs: 160, sm: 220 } }}><Line data={chartData} /></Box>}
        </Box>

        <Box sx={{
          width: isSmDown ? '100%' : { xs: '40%', sm: 360, md: 320 },
          minWidth: 200,
          maxHeight: { xs: 200, sm: 220 },
          overflow: 'auto',
          position: isSmDown ? 'relative' : 'sticky',
          top: isSmDown ? 'auto' : 100,
          alignSelf: 'flex-start',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 1,
          p: 1
        }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>تاریخچه پاسخ‌ها</Typography>

          {answerHistory.length === 0 ? <Typography color="text.secondary">بدون پاسخ</Typography> : (
            <Stack spacing={1}>
              {answerHistory.map((a, i) => (
                <Card key={i} variant="outlined" sx={{ overflow: 'hidden' }}>
                  <CardContent sx={{ p: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>{a.questionText || a.QuestionContent || 'سوال'}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: { xs: '0.72rem', sm: '0.8rem' } }}>گزینــه: {a.selectedOption} · امتیاز: {a.score}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: { xs: '0.7rem', sm: '0.78rem' } }}>{compactDate(a.answerTime)}</Typography>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Box>
      </Box>

      <Box sx={{ maxHeight: { xs: 260, sm: 360 }, overflow: 'auto' }}>
        <Stack spacing={2}>
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">رکوردهای کلاسی</Typography>
              <Chip label={`${classRecords.length} رکورد`} size="small" />
            </Stack>

            {recordsLoading ? <CircularProgress size={20} /> : classRecords.length === 0 ? <Typography color="text.secondary">بدون رکورد</Typography> : (
              <Box sx={{ maxHeight: { xs: 140, sm: 200 }, overflow: 'auto', mt: 1 }}>
                {classRecords.map(r => (
                  <Card key={r.id} sx={{ mb: 1 }}>
                    <CardContent sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start', p: 1 }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', sm: '1rem' } }}>{persianType(r.type)} {r.lessonTitle ? `— ${r.lessonTitle}` : ''} — {compactDate(r.date)}</Typography>
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>{r.description}</Typography>
                        {r.score !== null && r.score !== undefined && <Typography variant="caption">نمره: {r.score}</Typography>}
                      </Box>
                      <Stack spacing={1} sx={{ ml: 1 }}>
                        <IconButton color="error" onClick={() => handleDeleteRecord(r.id)} size="small"><DeleteIcon fontSize="small" /></IconButton>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            {/* add record form (editable) */}
            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                select
                label="نوع"
                size="small"
                value={newRecord.type}
                onChange={(e) => setNewRecord({ ...newRecord, type: e.target.value })}
                sx={{ minWidth: { xs: 120, sm: 140 }, width: { xs: '48%', sm: 'auto' } }}
              >
                <MenuItem value="Positive">مثبت</MenuItem>
                <MenuItem value="Negative">منفی</MenuItem>
                <MenuItem value="ClassExam">امتحان کلاسی</MenuItem>
                <MenuItem value="Note">یادداشت</MenuItem>
              </TextField>

              <TextField
                select
                label="درس (اختیاری)"
                size="small"
                value={newRecord.lessonId}
                onChange={(e) => setNewRecord({ ...newRecord, lessonId: e.target.value })}
                sx={{ minWidth: { xs: 140, sm: 180 }, width: { xs: '48%', sm: 'auto' } }}
              >
                <MenuItem value="">بدون درس</MenuItem>
                {studentLessonsForForm.length === 0
                  ? <MenuItem disabled>{'درس‌‌یافت نشد'}</MenuItem>
                  : studentLessonsForForm.map(l => <MenuItem key={l.id} value={l.id}>{l.title}</MenuItem>)}
              </TextField>

              <TextField
                type="date"
                label="تاریخ"
                size="small"
                value={newRecord.date}
                onChange={(e) => setNewRecord({ ...newRecord, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: '48%', sm: 170 } }}
              />

              <TextField
                label="توضیح"
                placeholder="توضیح..."
                size="small"
                value={newRecord.description}
                onChange={(e) => setNewRecord({ ...newRecord, description: e.target.value })}
                sx={{ minWidth: { xs: '100%', sm: 220 }, flex: 1 }}
              />

              <TextField
                label="نمره (اختیاری)"
                type="number"
                size="small"
                value={newRecord.score}
                onChange={(e) => setNewRecord({ ...newRecord, score: e.target.value })}
                sx={{ width: { xs: '48%', sm: 140 } }}
              />

              <Button
                variant="contained"
                sx={{ bgcolor: 'primary.main', minWidth: { xs: 96, sm: 120 } }}
                onClick={() => handleAddRecord(newRecord, selectedStudentId)}
                disabled={!selectedStudentId}
              >
                افزودن
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />
        </Stack>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto', bgcolor: 'background.default', minHeight: '100vh' }} dir="rtl">
      <Paper sx={{
        p: { xs: 1, sm: 2 },
        mb: 2,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 1, md: 2 },
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'background.paper'
      }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
          <Avatar sx={{ bgcolor: '#1976d2', width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 }, fontSize: { xs: '1rem', sm: '1.25rem' } }}>{(email || 'م').charAt(0)}</Avatar>
          <Box>
            <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem' }, fontWeight: 600, color: 'text.primary' }}>
              خوش آمدید، {email}
            </Typography>
            <Typography variant="caption" color="text.secondary">داشبورد معلم — مدیریت دانش‌آموز و گزارش‌ها</Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: { xs: 1, md: 0 }, width: { xs: '100%', md: 'auto' }, justifyContent: { xs: 'space-between', md: 'flex-end' } }}>
          <ThemeToggle />
          <Button variant="outlined" onClick={() => navigate('/questions')} sx={{ minWidth: { xs: 80, sm: 120 } }}>مدیریت سوال‌ها</Button>
          <Button variant="outlined" onClick={() => navigate('/add-question')} sx={{ minWidth: { xs: 80, sm: 120 } }}>طرح سوال</Button>
          <Button variant="outlined" onClick={() => navigate('/attendance')} startIcon={<TodayIcon />} sx={{ minWidth: { xs: 90, sm: 140 } }}>حضور و غیاب</Button>
          <Button color="error" variant="contained" onClick={handleLogout} sx={{ bgcolor: 'error.main', minWidth: { xs: 72, sm: 96 } }}>خروج</Button>
        </Stack>
      </Paper>

      <Box sx={{ display: { xs: 'block', md: 'flex' }, gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{
          width: leftColumnWidth,
          maxWidth: leftColumnMaxWidth,
          flexShrink: 0,
          maxHeight: `calc(100vh - ${headerHeight}px)`,
          overflow: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 2,
          p: { xs: 1, sm: 1.5 },
          mb: { xs: 2, md: 0 }
        }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1rem' } }}>دانش‌آموزان</Typography>
              <Chip label={`${(selectedLessonId ? lessonStudents.length : students.length)}`} size="small" />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              {selectedStudent ? <Typography variant="caption" color="text.secondary" sx={{ ml: 1, display: { xs: 'none', sm: 'block' } }}>انتخاب‌شده: {selectedStudent.name}</Typography> : null}
              <Tooltip title={studentsOpen ? 'بستن لیست' : 'باز کردن لیست'}>
                <IconButton size="small" onClick={() => setStudentsOpen(open => !open)}>
                  <ExpandMoreIcon sx={{ transform: studentsOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: '200ms transform' }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {studentsOpen && (
            <Box>
              <Box sx={{ mb: 1 }}>
                <TextField
                  placeholder="جستجو بر اساس نام یا کد..."
                  size="small"
                  fullWidth
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>) }}
                />
              </Box>

              <Box sx={{ mb: 1 }}>
                <TextField
                  select
                  size="small"
                  label="فیلتر درس"
                  value={selectedLessonId}
                  onChange={(e) => setSelectedLessonId(e.target.value)}
                  sx={{ minWidth: 160, width: '100%' }}
                  helperText={lessonsLoading ? 'در حال بارگذاری...' : lessons.length === 0 ? 'هیچ درسی یافت نشد' : ''}
                >
                  <MenuItem value="">همه درس‌ها</MenuItem>
                  {lessons.map(l => (<MenuItem key={String(l.id)} value={String(l.id)}>{l.title ?? l.Title ?? l.name ?? 'بدون عنوان'}</MenuItem>))}
                </TextField>
              </Box>

              <Divider sx={{ mb: 1 }} />

              <Box sx={{ pr: 1 }}>
                { (selectedLessonId && lessonStudentsLoading) ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>
                ) : visibleStudents.length === 0 ? (
                  <Typography color="text.secondary">دانش‌آموزی پیدا نشد.</Typography>
                ) : visibleStudents.map(st => (
                  <Card key={st.id} variant="outlined" sx={{
                    mb: 1,
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 2 },
                    px: 0
                  }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 1 }}>
                      <Box onClick={() => selectStudent(st.id)} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'text.primary', fontSize: { xs: '0.95rem', sm: '1rem' } }}>{st.name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>کد: {st.code}</Typography>
                        {st.lessons && st.lessons.length > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            درس‌ها: {st.lessons.map(l => l.title || l.name).slice(0, 3).join('، ')}{st.lessons.length > 3 ? ' ...' : ''}
                          </Typography>
                        )}
                        {st.recordsCount !== undefined && <Typography variant="caption" color="text.secondary" display="block">رکوردها: {st.recordsCount}</Typography>}
                      </Box>

                      <Stack direction="column" spacing={0.5} alignItems="center" sx={{ ml: 1 }}>
                        <Tooltip title="مشاهده">
                          <IconButton size="small" onClick={() => selectStudent(st.id)}><OpenInNewIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="اشتراک با والد (کپی لینک)">
                          <IconButton size="small" color="primary" onClick={() => handleShareLink(st)}><ShareIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>

              {selectedLessonId && lessonStudents.length > 0 && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  <Button size="small" disabled={lessonStudentsMeta.page <= 1} onClick={() => fetchLessonStudents({ lessonId: selectedLessonId, page: lessonStudentsMeta.page - 1, pageSize: lessonStudentsMeta.pageSize })}>صفحه قبل</Button>
                  <Typography variant="caption">صفحه {lessonStudentsMeta.page} از {Math.max(1, Math.ceil(lessonStudentsMeta.totalCount / lessonStudentsMeta.pageSize))}</Typography>
                  <Button size="small" disabled={lessonStudentsMeta.page >= Math.ceil(lessonStudentsMeta.totalCount / lessonStudentsMeta.pageSize)} onClick={() => fetchLessonStudents({ lessonId: selectedLessonId, page: lessonStudentsMeta.page + 1, pageSize: lessonStudentsMeta.pageSize })}>صفحه بعد</Button>
                </Stack>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ flex: 1, maxHeight: `calc(100vh - ${headerHeight}px)`, overflow: 'auto' }}>
          {!selectedStudentId ? (
            <Paper sx={{ p: 3, minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.paper' }}>
              <Box textAlign="center">
                <Typography variant="h6" sx={{ mb: 1 }}>یک دانش‌آموز انتخاب کنید</Typography>
                <Typography variant="body2" color="text.secondary">برای دیدن گزارش و دانلود فایل HTML روی یک دانش‌آموز کلیک کنید.</Typography>
              </Box>
            </Paper>
          ) : loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : (
            <Paper sx={{ p: 0, bgcolor: 'background.paper' }}>
              <StudentDetailContent />
            </Paper>
          )}
        </Box>
      </Box>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} ModalProps={{ keepMounted: true }} PaperProps={{ sx: { width: '100%', maxWidth: 640 } }}>
        <Box sx={{ p: 1 }}>
          <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setDrawerOpen(false)}>بستن</Button>
          </Box>
          {selectedStudentId ? <StudentDetailContent /> : <Typography sx={{ p: 2 }}>هیچ دانش‌آموزی انتخاب نشده است.</Typography>}
        </Box>
      </Drawer>

      <Dialog open={questionModalOpen} onClose={() => setQuestionModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>ثبت سوال جدید</DialogTitle>
        <DialogContent dividers>
          <TextField fullWidth label="متن سوال" multiline rows={3} value={questionForm.content} onChange={(e) => setQuestionForm(prev => ({ ...prev, content: e.target.value }))} sx={{ mb: 1 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
            <TextField label="گزینه A" fullWidth value={questionForm.optionA} onChange={(e) => setQuestionForm(prev => ({ ...prev, optionA: e.target.value }))} />
            <TextField label="گزینه B" fullWidth value={questionForm.optionB} onChange={(e) => setQuestionForm(prev => ({ ...prev, optionB: e.target.value }))} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
            <TextField label="گزینه C" fullWidth value={questionForm.optionC} onChange={(e) => setQuestionForm(prev => ({ ...prev, optionC: e.target.value }))} />
            <TextField label="گزینه D" fullWidth value={questionForm.optionD} onChange={(e) => setQuestionForm(prev => ({ ...prev, optionD: e.target.value }))} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
            <TextField label="گزینه صحیح (A/B/C/D)" value={questionForm.correctOption} onChange={(e) => setQuestionForm(prev => ({ ...prev, correctOption: e.target.value }))} sx={{ minWidth: 120 }} />
            <TextField select label="درس" value={questionForm.lessonId} onChange={(e) => setQuestionForm(prev => ({ ...prev, lessonId: e.target.value }))} sx={{ minWidth: 160 }}>
              <MenuItem value="">انتخاب کنید</MenuItem>
              {lessonsLoading ? <MenuItem disabled>در حال بارگذاری...</MenuItem> : lessons.length === 0 ? <MenuItem disabled>درسی یافت نشد</MenuItem> : lessons.map(l => (<MenuItem key={l.id ?? l.Id ?? l._id} value={l.id ?? l.Id ?? l._id}>{l.title ?? l.Title ?? l.name ?? 'بدون عنوان'}</MenuItem>))}
            </TextField>
            <TextField type="datetime-local" label="مهلت (اختیاری)" InputLabelProps={{ shrink: true }} value={questionForm.expirationDate} onChange={(e) => setQuestionForm(prev => ({ ...prev, expirationDate: e.target.value }))} sx={{ minWidth: 200 }} />
          </Stack>

          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={loadQuestions}>بارگذاری سؤالات</Button>
            <Button variant="text" onClick={() => { setQuestionModalOpen(false); navigate('/add-question'); }}>فرم کامل طرح سوال</Button>
          </Box>

          {questionsLoading ? <CircularProgress size={20} sx={{ mt: 1 }} /> : questions.length > 0 && (
            <Box sx={{ mt: 1, maxHeight: 240, overflow: 'auto' }}>
              {questions.map(qn => (
                <Card key={qn.id} sx={{ mb: 1 }}>
                  <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', sm: '1rem' } }}>{qn.content}</Typography>
                      <Typography variant="caption" color="text.secondary">درس: {qn.lessonId} · مهلت: {qn.expirationDate ? new Date(qn.expirationDate).toLocaleString() : '—'}</Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" onClick={() => navigate(`/edit-question/${qn.id}`)}>ویرایش</Button>
                      <Button size="small" color="error" onClick={() => handleDeleteQuestion(qn.id)}>حذف</Button>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setQuestionModalOpen(false)}>انصراف</Button>
          <Button variant="contained" onClick={handleSubmitQuestion} sx={{ bgcolor: 'primary.main' }}>ثبت سوال</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}